import { ExpiryType, getFormatter, ListedInstrument, OrderType, useDictionary } from '@eih-netbroker/common';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { OrderConfirmRow } from '../../components/order/OrderConfirmRow';
import { ValidatedOrder } from './useNormalOrderView';

export interface OrderConfirmData extends ValidatedOrder {
  accountName: string;
}

export const orderRows: Array<keyof OrderConfirmData | keyof ListedInstrument> = [
  'accountName',
  'instrumentName',
  'isin',
  'currency',
  'tradeType',
  'orderType',
  'quantity',
  'price',
  'stopActivationPrice',
  'expiryType',
  'tradingSession',
];

export const useOrderRows = () => {
  const { translation } = useLocale();
  const { formatNumberF2, formatDate } = getFormatter();
  const { formatDictionaryType } = useDictionary();

  const unknownValue = '';

  const getOrderRows = (order: OrderConfirmData, row: keyof OrderConfirmData | keyof ListedInstrument) => {
    switch (row) {
      case 'accountName':
        return (
          <OrderConfirmRow
            label={translation.orderConfirm.rows.accountName}
            testIdBase="order-confirm-account-name"
            value={order.accountName}
          />
        );
      case 'instrumentName':
        return (
          <OrderConfirmRow
            label={translation.orderConfirm.rows.instrumentName}
            testIdBase="order-confirm-instrument-name"
            value={order.instrument?.instrumentName}
          />
        );
      case 'isin':
        return (
          <OrderConfirmRow
            label={translation.orderConfirm.rows.isin}
            testIdBase="order-confirm-isin"
            value={order.instrument?.isin}
          />
        );
      case 'currency':
        return (
          <OrderConfirmRow
            label={translation.orderConfirm.rows.currencyCode}
            testIdBase="order-confirm-currency"
            value={order.instrument?.currency}
            isDivider
          />
        );
      case 'orderType':
        return (
          <OrderConfirmRow
            label={translation.orderConfirm.rows.orderType}
            testIdBase="order-confirm-order-type"
            value={formatDictionaryType(order.orderType, 'orderTypes')}
          />
        );
      case 'tradeType':
        return (
          <OrderConfirmRow
            label={translation.orderConfirm.rows.tradeType}
            testIdBase="order-confirm-trade-type"
            value={translation.orderConfirm.rows.tradeTypeValues[order.tradeType!].toUpperCase()}
          />
        );
      case 'quantity':
        return (
          <OrderConfirmRow
            label={translation.orderConfirm.rows.quantity}
            testIdBase="order-confirm-quantity"
            value={order.quantity + ' db'}
          />
        );
      case 'price':
        return (
          <OrderConfirmRow
            label={translation.orderConfirm.rows.exchangeRate}
            testIdBase="order-confirm-price"
            value={
              order.orderType === OrderType.Market ? translation.orderConfirm.market : formatNumberF2(order.price!)
            }
          />
        );
      case 'stopActivationPrice':
        return (
          <OrderConfirmRow
            label={translation.orderConfirm.rows.stopActivationPrice}
            testIdBase="order-confirm-stop-activation-price"
            value={formatNumberF2(order.stopActivationPrice ?? unknownValue)}
          />
        );
      case 'expiryType':
        return (
          <OrderConfirmRow
            label={translation.orderConfirm.rows.expiryType}
            testIdBase="order-confirm-expiry-type"
            value={
              order.expiryType === ExpiryType.Gtd
                ? formatDate(order.expiry!)
                : formatDictionaryType(order.expiryType, 'orderExpiryTypes').toUpperCase()
            }
          />
        );
      case 'tradingSession':
        return (
          <OrderConfirmRow
            label={translation.orderConfirm.rows.tradingSession}
            testIdBase="order-confirm-trading-session"
            value={formatDictionaryType(order.tradingSession!, 'tradingSessions')}
          />
        );
      default:
        throw new Error(`Unknow error: ${row}`);
    }
  };

  return { getOrderRows };
};
